import { Field as BaseField } from 'components/Wizards/Field/Field'
import { BrandKitField } from 'components/Wizards/Wizard/Form/BrandKitField/BrandKitField'
import { SnippetTypeaheadInput } from 'components/Wizards/Wizard/Form/SnippetTypeaheadInput'
import { FieldSchema } from 'components/Wizards/Wizard/Form/Types'
import Styles from './DesktopTopic.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { ArrowRefresh2 } from 'blaze-ui/icons'
import { useEffect, useState } from 'react'
import { useFormikContext } from 'formik'
import { useWizardSchema } from 'components/Wizards/Wizard/WizardContext'
import { MultiStepSchema } from 'components/Wizards/schemas/Types'
import { AIJobStatus } from 'types'
import { PollFieldsetCompletion } from 'components/PollFieldsetCompletion/PollFieldsetCompletion'
import { Button } from 'blaze-ui/Button'
import { useAutocomplete } from 'components/Wizards/DesktopWizards/useAutocomplete'
import { ConditionalLanguageInputField as LanguageInputField } from '../../../../Wizard/Form/LanguageInputField/ConditionalLanguageInputField'

interface Props {
  topicSchema: FieldSchema
  onLoadingChange?: (value: boolean) => void
  fields: string[]
  showLanguage?: boolean
}

interface Values {
  topic?: string
  language?: string
}

export const DesktopTopic = ({ topicSchema, onLoadingChange, fields, showLanguage }: Props) => {
  const { nullable, optional, meta } = topicSchema.describe()
  const schema = useWizardSchema() as MultiStepSchema
  const [isLoading, setIsLoading] = useState(false)
  const { values: formikValues, setStatus, initialStatus, setFieldValue } = useFormikContext<{ [key: string]: any }>()
  const [writingOwn, setWritingOwn] = useState(false)

  const isRequired = !nullable && !optional
  const values = formikValues as Values

  const handleLoadingChange = (value: boolean) => {
    onLoadingChange?.(value)
    setIsLoading(value)
  }

  const { handleComplete, handleGenerate, data } = useAutocomplete({
    onLoadingChange: handleLoadingChange,
    schema,
    setStatus,
    setFieldValue,
    initialStatus,
    values: formikValues,
  })

  useEffect(() => {
    if (!values.topic && values.language) {
      handleLoadingChange(true)
      handleGenerate({ fields })
    }
  }, [values.language]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PollFieldsetCompletion fieldsetCompletionId={data?.fieldset_completion.id} onComplete={handleComplete}>
      {({ fieldsetCompletion }) => {
        const isGenerationDisabled =
          isLoading ||
          (fieldsetCompletion && [AIJobStatus.Pending, AIJobStatus.Started].includes(fieldsetCompletion.status))
        return (
          <BaseField
            name="topic"
            label={meta.label}
            description={meta.description}
            direction="column"
            isRequired={isRequired}
            rightContent={
              <Button
                isDisabled={isGenerationDisabled}
                frontIcon={ArrowRefresh2}
                variant="ghost"
                size="sm"
                onPress={() => {
                  handleGenerate({ fields: ['topic'] })
                  setWritingOwn(false)
                }}
              >
                Regenerate
              </Button>
            }
          >
            <SnippetTypeaheadInput
              name="topic"
              schema={topicSchema}
              autoFocus
              isLoading={isGenerationDisabled}
              typeaheadDisabled
              setWritingOwn={setWritingOwn}
              actions={
                <div className={Styles.rightContent}>
                  {showLanguage && (
                    <div className={Styles.languageField}>
                      <div className={Styles.label}>
                        <Text variant="secondary" color="var(--dark-40)">
                          Language
                        </Text>
                      </div>
                      <div className={Styles.languageFieldButton}>
                        <LanguageInputField label="" />
                      </div>
                    </div>
                  )}

                  <div className={Styles.brandKitField}>
                    <div className={Styles.label}>
                      <Text variant="secondary" color="var(--dark-40)">
                        Brand Kit
                      </Text>
                    </div>
                    <div className={Styles.brandKitFieldButton}>
                      <BrandKitField
                        size="sm"
                        onChange={(brandKitId) =>
                          !writingOwn &&
                          handleGenerate({
                            fields: ['topic', 'filename'],
                            newCompletedFields: { brand_kit_id: brandKitId },
                          })
                        }
                        isDisabled={isGenerationDisabled}
                      />
                    </div>
                  </div>
                </div>
              }
            />
          </BaseField>
        )
      }}
    </PollFieldsetCompletion>
  )
}
